import { FC, ReactNode } from 'react';
import { useRbacPermission } from '../../hooks/useRbacPermissionHook';
import { useRbacState } from '../../context/rbac.context';
import { hasAnySubmoduleReadPermission } from '../../utils/rbacHelpers';

export type PermissionType = 'read' | 'write' | 'delete' | 'any';

interface PermissionGateProps {
  module: string;
  submodule?: string;
  permission: PermissionType;
  /** When true, `submodule` is ignored; any read in the module grants access. */
  checkAnySubmodule?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
  showErrorOnDenied?: boolean;
}

/**
 * Component that conditionally renders children based on RBAC permissions
 * @param module - Module name (e.g., 'students', 'staff')
 * @param submodule - Submodule name (e.g., 'studentList', 'attendance')
 * @param permission - Type of permission to check ('read', 'write', 'delete', or 'any')
 * @param children - Content to render if permission is granted
 * @param fallback - Optional content to render if permission is denied
 * @param showErrorOnDenied - If true, shows error message when permission is denied (default: false)
 */
export const PermissionGate: FC<PermissionGateProps> = ({
  module,
  submodule = '',
  permission,
  checkAnySubmodule = false,
  children,
  fallback = null,
  showErrorOnDenied = false,
}) => {
  const { rbac, isLoading: rbacLoading, errorMessage } = useRbacState();
  const { canRead, canWrite, canDelete, hasAny, isLoading } = useRbacPermission(
    module,
    submodule
  );

  // Handle edge cases: loading or error states
  if (rbacLoading || isLoading) {
    return null;
  }

  // If RBAC failed to load, default to no access (do not treat missing rbac alone as failure)
  if (!rbac && errorMessage) {
    return <>{fallback}</>;
  }

  if (!rbac) {
    return null;
  }

  let hasPermission = false;

  if (checkAnySubmodule) {
    hasPermission =
      permission === 'read' || permission === 'any'
        ? hasAnySubmoduleReadPermission(rbac, module)
        : false;
  } else {
    switch (permission) {
      case 'read':
        hasPermission = canRead;
        break;
      case 'write':
        hasPermission = canWrite;
        break;
      case 'delete':
        hasPermission = canDelete;
        break;
      case 'any':
        hasPermission = hasAny;
        break;
      default:
        hasPermission = false;
    }
  }

  if (!hasPermission && showErrorOnDenied) {
    // Error will be shown by the calling component using usePermissionDeniedMessage
    return <>{fallback}</>;
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};
